import { createAsync, query, revalidate, useNavigate, A } from "@solidjs/router"
import { createEffect, createMemo, createSignal, For, onMount, Show } from "solid-js";

import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'

TimeAgo.addDefaultLocale( en );

import { Dropdown } from "~/components";
import { formattedTimestamp, isDev } from "~/lib";
import { ensureCognitoUser } from "~/_server/cognito";
import { schemeChips } from "~/lib/schemeChip";

const capitalize = ( str: string ) =>
	str.charAt( 0 ).toUpperCase() + str.slice( 1 ).toLowerCase();

const NOW = Date.now();
const MONTH_IN_SECONDS = 1000  * 60 * 60 * 24 * 28;
const BASE_DATE = NOW - ( MONTH_IN_SECONDS * 12 * 30 );


type UserSort = 'name' | 'lastLogin' | 'email' | 'role' | 'status' | 'registered';

// The user record's own status stops being updated once a subscription exists,
// so show the subscription's state where there is one. See lib/userStatus.ts.
const getUsersWithStatus = query( async () => {
	"use server";

	const { fetchAll } = await import( "~/_server/stats" );
	const { deriveUserStatus } = await import( "~/lib/userStatus" );

	const [users, subs] = await Promise.all( [
		fetchAll( "USER", "pk, sk, #name, lastLogin, firstName, lastName, email, #role, userId, #status, registered, #sub, targetScheme",
			{ "#name": "name", "#role": "role", "#status": "status", "#sub": "sub" } ),
		fetchAll( "SUBSCRIPTION", "pk, scheme, #status", { "#status": "status" } ),
	] );

	const subsByUser: Record<string, any[]> = {};

	for ( const sub of subs ) ( subsByUser[sub.pk] ??= [] ).push( sub );

	return users.map( u => {
		const userSubs = subsByUser[u.pk] ?? [];
		const derived = deriveUserStatus( u, userSubs );

		// Subscriptions where they exist; an applicant has only targetScheme.
		const schemes = userSubs.length ? userSubs.map( x => x.scheme ) : [u.targetScheme];

		return { ...u, schemes, displayStatus: derived.status, statusDetail: derived.detail, statusFromUser: derived.fromUserRecord };
	} );
}, "getUsersWithStatus" );

export const Users = props => {
	const [filter, setFilter] = createSignal( '' );
	const [sort, setSort] = createSignal<UserSort>( 'registered' );
	const [sortDir, setSortDir] = createSignal<'asc' | 'desc'>( 'desc' );
	const [timeLimit, setTimeLimit] = createSignal( NOW - ( MONTH_IN_SECONDS * 1 ) );

	const timeAgo = new TimeAgo( 'en-GB' );

	const users = createAsync( () => getUsersWithStatus() );
	
	const filteredUsers = createMemo( () => users()?.filter( u => {
		if ( filter() != "" ) {
			if ( u.name.toLowerCase().includes( filter().toLowerCase() ) || u.email.toLowerCase().includes( filter().toLowerCase() ) ) return true;
		} else {

			if ( ( u.lastLogin ?? BASE_DATE ) >= timeLimit() ) return true;
		}

		return false;
	} ) );

	const sortedUsers = createMemo( () => filteredUsers()?.sort( ( a, b ) => {
		switch( sort() ) {
			case 'name':
				return a.lastName.localeCompare( b.lastName ) || a.firstName.localeCompare( b.firstName );

			// Subtracting two strings only ever gave NaN, so this column never sorted.
			case 'status':
				return String( a.displayStatus ?? "" ).localeCompare( String( b.displayStatus ?? "" ) );

			default:
				return ( b[sort()] ?? 0 ) - ( a[sort()] ?? 0 );
		}
	} ) );
	
	return ( <div class="px-4 sm:px-6 lg:px-8">
		<div class="sm:flex sm:items-center">
			<div class="sm:flex-auto">
				<h1 class="text-base font-semibold text-gray-900">Users</h1>
			</div>
		</div>

		<div class="sm:flex sm:items-center text-sm mt-2">
			Filter: <input title="Search users" onInput={e => setFilter( e.target.value )} type="text" class="block rounded-md min-w-2xl ml-2 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6 w-10" />

			<Dropdown
				id="TimeLimit"
				class="mx-2 my-2"
				bgColour="bg-white"
				txtColour="text-black"
				value={timeLimit()}
				list={[{ value: NOW - (MONTH_IN_SECONDS * 12 * 40), label: 'All' }, { value: NOW - ( MONTH_IN_SECONDS * 1 ), label: 'Last 4 weeks' }, { value: NOW - (MONTH_IN_SECONDS * 6), label: 'Last 6 months' }, { value: NOW - (MONTH_IN_SECONDS * 12), label: 'Last 12 months' }, { value: NOW - (MONTH_IN_SECONDS * 24), label: 'Last 2 years' }]}
				onSelect={v => setTimeLimit( v )}
			/>
			<div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
				<button type="button" class="block rounded-md bg-sky-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-sky-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600">Add user</button>
			</div>

		</div>

		<div class="mt-8 flow-root">
			<div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
				<div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
					<table class="relative min-w-full divide-y divide-gray-300">
						<thead>
							<tr class="even:bg-gray-50">
								<th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0">
									<a href="#" class="group inline-flex">
										Name

										<span class="ml-2 flex-none rounded-sm bg-gray-100 text-gray-900 group-hover:bg-gray-200">
											<svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true" class="size-5">
												<path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" fill-rule="evenodd" />
											</svg>
										</span>
									</a>
								</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Login</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Registered</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Access</th>
							</tr>
						</thead>
						<tbody class="divide-y divide-gray-200">
							<For each={sortedUsers()}>
								{
									user => (
										<tr class="even:bg-gray-50">
											<td class="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-0"><A href={`/dashboard/admin/users/${user.userId}`}>{user.name}</A> (#{user.userId})
											<For each={schemeChips( user.schemes )}>
												{chip => (
													<span
														title={chip.label}
														class={`ml-1 inline-block rounded-sm px-1 text-[9.5px] font-bold align-middle ${chip.classes}`}>
														{chip.letter}
													</span>
												)}
											</For>
										</td>
											<td class="px-3 py-4 text-sm text-gray-500">{user.lastLogin ? timeAgo.format( parseInt( user.lastLogin ) ) : 'n/a'}</td>
											<td class="px-3 py-4 text-sm text-gray-500">{user.email}</td>
											<td class="px-3 py-4 text-sm text-gray-500">{JSON.stringify( user.roles )}</td>
											<td class="px-3 py-4 text-sm text-gray-500" title={user.statusDetail}>
											{user.displayStatus}
											<Show when={user.statusFromUser}><span class="text-gray-300"> *</span></Show>
										</td>
											<td class="px-3 py-4 text-sm text-gray-500">{formattedTimestamp( user.registered )}</td>
											<td class="px-3 py-4 text-sm text-gray-500">
												<Show when={user.sub}>
													✅
												</Show>
												<Show when={!user.sub}>
													<span onClick={async () => {
														// The same helper verification uses, so the two cannot drift apart.
														await ensureCognitoUser( user );

														revalidate( "getUsersWithStatus" );
													}}>➕</span>

												</Show>

												<Show when={isDev}>
													<a href={`/api/dev/login?profile=${user.userId}`} class="text-sky-600 hover:text-sky-900 cursor-pointer ml-2">&#128373;</a>
												</Show>
											</td>

											<td class="py-4 pr-4 pl-3 text-right text-sm font-medium sm:pr-0">
												<a href={`/api/dev/login?profile=${user.userId}`} class="text-sky-600 hover:text-sky-900 cursor-pointer ml-2">&#128373;</a>
											</td>
											{/* 
											<td class="py-4 pr-4 pl-3 text-right text-sm font-medium sm:pr-0">
												<button type="button" onClick={async () => setImpersonation( user.userId )} class="text-sky-600 hover:text-sky-900 cursor-pointer">Dashboard<span class="sr-only">, {user.name}</span>
													<Show when={sessionId() == user.userId}>
														<svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true" class="-mr-0.5 size-5">
															<path d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clip-rule="evenodd" fill-rule="evenodd" />
														</svg>
													</Show>
												</button>
											</td>*/}
										</tr>
									)
								}
							</For>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div> );
}